/**
 * Builders for the AI-search result-click telemetry event (PR-A3 remainder).
 *
 * The payload contract is docs/contracts/click-event.schema.json in the
 * BTNet-Search repo (canonical); a verbatim copy is committed next to this
 * file at ./contracts/click-event.schema.json and the tests validate every
 * built payload against it. The event is emitted through the existing
 * analytics channel (useTracking → LinkClick extraInfo.clickEvent) and joined
 * server-side to the search's OTel span tree into training triples
 * (query, shown_ranking, clicked_id, rank).
 */
import type { ISearchHit } from "./types";

/** Contract version of the click event. Major is pinned by the schema. */
export const CLICK_EVENT_SCHEMA_VERSION = "1.0";

/** Cap on the serialized `shown` list. `n_shown` still reports the true count. */
export const MAX_SHOWN_RESULTS = 100;

/** Server OTel trace id: exactly 32 lowercase hex chars (after case-folding). */
const TRACE_ID_PATTERN = /^[0-9a-f]{32}$/;

/**
 * Must match the server sanitizer (app/services/tracing.sanitize_client_search_id):
 * 1-64 chars of [A-Za-z0-9._:-]. A value the server dropped can never join.
 */
const SEARCH_ID_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

/** Schema maxLength for the optional `query` debug echo. */
const MAX_QUERY_LENGTH = 512;

/** Schema maxLength for the optional `builder_id`. */
const MAX_BUILDER_ID_LENGTH = 32;

/** One row of the visible result list, in display order. */
export interface IShownResult {
    entity: string;
    record_id: string;
}

/**
 * A contract-valid SearchResultClickEvent
 * (./contracts/click-event.schema.json, schema_version 1.0).
 */
export interface ISearchClickEvent {
    schema_version: string;
    search_id: string;
    trace_id: string | null;
    record_id: string;
    entity: string;
    rank: number;
    n_shown: number;
    shown: IShownResult[];
    clicked_at: string;
    builder_id: string | null;
    query: string | null;
}

/**
 * Normalize a trace id received from the server's `trace` SSE event.
 * Case-folds to lowercase (hex casing carries no information; the schema
 * pins lowercase) and returns null for anything that is not a valid
 * 32-hex-char OTel trace id — the contract requires clicks to be emitted
 * even when the trace never arrived (trace_id null), and a malformed id
 * would make the whole event schema-invalid.
 */
export function normalizeTraceId(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const folded = value.toLowerCase();
    return TRACE_ID_PATTERN.test(folded) ? folded : null;
}

/**
 * Normalize the canonical `search_id` echoed by the server on the trace SSE
 * event. Returns null unless it satisfies the server sanitizer's pattern —
 * an id the server dropped as invalid can never join, so callers fall back
 * to the client-minted uuid.
 */
export function normalizeSearchId(value: unknown): string | null {
    return typeof value === "string" && SEARCH_ID_PATTERN.test(value) ? value : null;
}

export interface IBuildClickEventParams {
    /**
     * The search this click belongs to — the same value sent as `search_id`
     * in the POST /v1/search body (or the server's canonical echo of it).
     */
    searchId: string;
    /** Trace id from the server's trace SSE event; null/undefined when it never arrived. */
    traceId: string | null | undefined;
    /** The clicked hit. `entity`/`id` are carried verbatim — no client-side folding. */
    hit: Pick<ISearchHit, "entity" | "id">;
    /** 0-based position of the clicked row within `visible`. */
    index: number;
    /**
     * The visible committed result list at click time, in display order —
     * AFTER client-side filtering (excluded entities, unroutable hits), since
     * that is exactly what the user was shown.
     */
    visible: readonly Pick<ISearchHit, "entity" | "id">[];
    /** Query text that produced the shown results (debug echo; truncated to 512). */
    query?: string | null;
    /** Builder (tenant) id when the caller has it. Serialized as a string. */
    builderId?: number | string | null;
}

/**
 * Build a contract-valid result-click event.
 *
 * Notes on field handling:
 * - `entity` and `record_id` MUST be echoed verbatim from the search
 *   response: the server-side join matches them with PLAIN EQUALITY and does
 *   NOT normalize casing, separators, or plurals — a folded value like
 *   "internalUser" or "internal_users" silently produces zero training
 *   triples. Do not run `normalizeKey()` (or any folding) on these. The
 *   legacy 278622 fields on the same LinkClick event (`searchResultType`,
 *   `uniqueId`) DO fold via `normalizeKey` — that asymmetry is intentional
 *   and separate; only this contract payload feeds the join.
 * - `rank` is the 1-based position of the clicked row WITHIN `shown` — the
 *   contract's hard invariant (`shown[rank-1]` is always the clicked row).
 *   For clicks inside the first 100 visible rows this equals the absolute
 *   visible position; see the windowing note below for clicks past 100.
 * - `shown` is capped at 100 rows. When the clicked row sits inside the
 *   top-100 prefix, `shown` IS that prefix (display order, top-down). When
 *   the click lands PAST the cap, a naive prefix would exclude the clicked
 *   row and `rank` would point outside `shown` — a corrupt training triple.
 *   Instead the 100-row window slides down, still contiguous and in display
 *   order, so it ENDS at the clicked row (rank = 100). `n_shown` always
 *   reports the true visible count, so the join can detect that the window
 *   was truncated/slid.
 * - `clicked_at` is RFC 3339 UTC with explicit offset ("Z").
 * - `trace_id` is null-safe: clicks are emitted even when the trace event
 *   never arrived, and malformed ids are folded to null.
 *
 * Returns null when `searchId` fails the server sanitizer's pattern (e.g.
 * the pre-first-search empty string): such an event could never join and
 * would be schema-INVALID, so it is skipped rather than emitted malformed.
 * Every other field is normalized/truncated into validity — search_id is
 * the one input this builder cannot repair.
 */
export function buildClickEvent(params: IBuildClickEventParams): ISearchClickEvent | null {
    const { searchId, traceId, hit, index, visible, query, builderId } = params;

    const validSearchId = normalizeSearchId(searchId);
    if (validSearchId === null) return null;

    const builderIdString = builderId == null ? "" : String(builderId);

    // Slide the serialization window down just far enough to contain the
    // clicked row (windowStart is 0 unless index >= MAX_SHOWN_RESULTS).
    const windowStart = Math.max(0, index + 1 - MAX_SHOWN_RESULTS);

    return {
        schema_version: CLICK_EVENT_SCHEMA_VERSION,
        search_id: validSearchId,
        trace_id: normalizeTraceId(traceId),
        record_id: hit.id,
        entity: hit.entity,
        rank: index - windowStart + 1,
        n_shown: visible.length,
        shown: visible.slice(windowStart, windowStart + MAX_SHOWN_RESULTS).map((h) => ({
            entity: h.entity,
            record_id: h.id,
        })),
        clicked_at: new Date().toISOString(),
        builder_id: builderIdString === "" ? null : builderIdString.slice(0, MAX_BUILDER_ID_LENGTH),
        query: query == null ? null : query.slice(0, MAX_QUERY_LENGTH),
    };
}
